/**
 * DeepReservesSkillEffect — Specter wraith-path Endurance upgrade: stamina max bonus.
 *
 * Upgrade enhancing Surge. Permanently increases maximum stamina by 30% while
 * equipped. The zero-drain window after Surge activation (2 seconds of free
 * running) is handled by SurgeSkillEffect reading the `zeroDrainWindowMs`
 * effect key through the resolver — this class only needs to manage the
 * always-on stamina max bonus.
 *
 * On equip: sets `staminaMaxBonus` on StaminaComponent (+30%).
 * On unequip: resets `staminaMaxBonus` to 0.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Deep Reserves
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DeepReservesDeps {
  readonly stamina: {
    /** Fractional bonus to max stamina (0 = no bonus, 0.3 = +30%). */
    staminaMaxBonus: number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DeepReservesConfig {
  /** Fractional bonus to max stamina (e.g. 0.3 = +30%). */
  readonly staminaMaxBonus: number;
}

const DEFAULT_CONFIG: DeepReservesConfig = {
  staminaMaxBonus: 0.3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DeepReservesSkillEffect implements SkillEffect {
  readonly skillId = 'deep_reserves';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: DeepReservesDeps;
  private readonly config: DeepReservesConfig;

  /** Whether the bonus has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: DeepReservesDeps, config: Partial<DeepReservesConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    // Apply the stamina max bonus on first update tick (equip).
    // Done here rather than constructor to match the FlowState pattern
    // where deps are guaranteed initialized by the first update call.
    if (!this._applied) {
      this.deps.stamina.staminaMaxBonus = this.config.staminaMaxBonus;
      this._applied = true;
    }
  }

  destroy(): void {
    // Reset stamina max bonus to neutral on unequip
    if (this._applied) {
      this.deps.stamina.staminaMaxBonus = 0;
      this._applied = false;
    }
  }
}
